import {
  SecurityGeoJsonFeature,
  SecurityGeoJsonPosition,
  SecuritySiteCountryModel,
} from "src/app/store/models/security-mapping.model";
import { inject, Injectable } from "@angular/core";
import { LocationModel, SiteModel } from "src/app/store/models/asset.model";
import { CheckinMapService } from "src/app/services/checkin-map-service/checkin-map.service";

// Same outline as the check-in map's countries and the Figma frame (26839:1041)
const COUNTRY_OUTLINE_COLOR = "#3CB4AA";
const COUNTRY_OUTLINE_WEIGHT = 3;

// Country of the checked-in site, outlined on the Security mapping maps (Settings
// preview and the mapping page). The countries GeoJSON is ~6 MB, so it is loaded
// only when first needed and the result is kept per site.
@Injectable({ providedIn: "root" })
export class SecurityMappingCountryService {
  private checkinMap = inject(CheckinMapService);

  private countriesRequest: Promise<SecurityGeoJsonFeature[]> | null = null;
  private countryRequestsBySite = new Map<
    string,
    Promise<SecuritySiteCountryModel | null>
  >();

  public GetSiteCountry(
    site: SiteModel | null,
  ): Promise<SecuritySiteCountryModel | null> {
    if (!site) return Promise.resolve(null);
    const siteKey = site.id ?? site.locations?.site?.unique_id ?? "";
    const cached = this.countryRequestsBySite.get(siteKey);
    if (cached) return cached;

    const country = site.locations?.country;
    const request = this.findCountry(
      [country?.aliasName, country?.location, country?.name],
      site.locations?.site?.coordinate ?? null,
    ).catch((error: unknown) => {
      console.warn("Security mapping: site country lookup failed", error);
      this.countryRequestsBySite.delete(siteKey);
      return null;
    });
    this.countryRequestsBySite.set(siteKey, request);
    return request;
  }

  // Outline only: not clickable, so map clicks inside the country still place points
  public DrawOutline(
    map: google.maps.Map,
    country: SecuritySiteCountryModel,
  ): google.maps.Polygon[] {
    return country.polygons.map(
      (rings) =>
        new google.maps.Polygon({
          map,
          zIndex: 0,
          paths: rings,
          fillOpacity: 0,
          clickable: false,
          strokeColor: COUNTRY_OUTLINE_COLOR,
          strokeWeight: COUNTRY_OUTLINE_WEIGHT,
        }),
    );
  }

  // The site's own country name (as the check-in map reads it); "" when it has none
  public GetSiteCountryName(site: SiteModel | null): string {
    const country = site?.locations?.country;
    return (country?.aliasName ?? country?.location ?? "").trim();
  }

  // Inside one of the country's polygons (outer ring) and not in one of its holes
  public IsInside(
    country: SecuritySiteCountryModel,
    position: google.maps.LatLngLiteral,
  ): boolean {
    const point: SecurityGeoJsonPosition = [position.lng, position.lat];
    const toRing = (
      ring: google.maps.LatLngLiteral[],
    ): SecurityGeoJsonPosition[] => ring.map(({ lat, lng }) => [lng, lat]);
    return country.polygons.some(
      ([outer, ...holes]) =>
        !!outer &&
        isInsideRing(point, toRing(outer)) &&
        !holes.some((hole) => isInsideRing(point, toRing(hole))),
    );
  }

  // Across the 180° line when that is narrower (New Zealand's Chatham Islands are at
  // -176°): extending one position at a time could span the whole world instead
  public GetBounds(
    country: SecuritySiteCountryModel,
  ): google.maps.LatLngBounds {
    const positions = country.polygons.flatMap((rings) => rings[0] ?? []);
    if (!positions.length) return new google.maps.LatLngBounds();
    const lats = positions.map((position) => position.lat);
    const { west, east } = getLongitudeSpan(
      positions.map((position) => position.lng),
    );
    return new google.maps.LatLngBounds({
      west,
      east,
      south: Math.min(...lats),
      north: Math.max(...lats),
    });
  }

  // By the country name first (aliasName, normalised to the GeoJSON names like the
  // check-in map), then by the polygon that contains the site's coordinate
  private async findCountry(
    countryNames: (string | undefined)[],
    coordinate: { lat?: number; lng?: number } | null,
  ): Promise<SecuritySiteCountryModel | null> {
    const features = await this.loadCountries();
    const names = countryNames
      .filter((name): name is string => !!name?.trim())
      .flatMap((name) => {
        const key = name.toLowerCase().trim();
        return [key, this.checkinMap.countries[key]?.toLowerCase() ?? key];
      });
    const sitePosition: SecurityGeoJsonPosition | null =
      coordinate?.lat != null && coordinate?.lng != null
        ? [coordinate.lng, coordinate.lat]
        : null;

    const feature =
      features.find((item) =>
        [item.properties?.name, item.properties?.admin].some(
          (name) => !!name && names.includes(name.toLowerCase()),
        ),
      ) ??
      (sitePosition
        ? features.find((item) =>
            toPolygons(item).some((rings) =>
              isInsideRing(sitePosition, rings[0] ?? []),
            ),
          )
        : undefined);
    if (!feature) return null;

    const { label_x: labelLng, label_y: labelLat } = feature.properties ?? {};
    return {
      name: feature.properties?.name ?? "",
      labelPosition:
        labelLat != null && labelLng != null
          ? { lat: labelLat, lng: labelLng }
          : null,
      polygons: toPolygons(feature).map((rings) =>
        rings.map((ring) => ring.map(([lng, lat]) => ({ lat, lng }))),
      ),
    };
  }

  private loadCountries(): Promise<SecurityGeoJsonFeature[]> {
    this.countriesRequest ??=
      import("src/app/utils/check-in-map/countries.geo.json")
        .then(
          (module) =>
            (
              module.default as unknown as {
                features: SecurityGeoJsonFeature[];
              }
            ).features,
        )
        .catch((error: unknown) => {
          this.countriesRequest = null;
          throw error;
        });
    return this.countriesRequest;
  }

  // A country chosen in the Country check-in (not the checked-in site's): by its name,
  // else by the position of one of its sites; kept per country
  public GetCountry(
    country: LocationModel,
    coordinate: google.maps.LatLngLiteral | null,
  ): Promise<SecuritySiteCountryModel | null> {
    const countryKey = `country:${country.unique_id}`;
    const cached = this.countryRequestsBySite.get(countryKey);
    if (cached) return cached;

    const request = this.findCountry(
      [country.aliasName, country.location, country.name],
      coordinate,
    ).catch(
      (error: unknown) => {
        console.warn("Security mapping: country lookup failed", error);
        this.countryRequestsBySite.delete(countryKey);
        return null;
      },
    );
    this.countryRequestsBySite.set(countryKey, request);
    return request;
  }

  // A BOG country by its name only (a combined check-in's country with no site, or the
  // Mapping page's countries); kept per name
  public GetCountryByName(
    name: string,
  ): Promise<SecuritySiteCountryModel | null> {
    const countryKey = `name:${name.trim().toLowerCase()}`;
    const cached = this.countryRequestsBySite.get(countryKey);
    if (cached) return cached;

    const request = this.findCountry([name], null).catch((error: unknown) => {
      console.warn("Security mapping: country lookup failed", error);
      this.countryRequestsBySite.delete(countryKey);
      return null;
    });
    this.countryRequestsBySite.set(countryKey, request);
    return request;
  }
}

function toPolygons(
  feature: SecurityGeoJsonFeature,
): SecurityGeoJsonPosition[][][] {
  return feature.geometry.type === "Polygon"
    ? [feature.geometry.coordinates]
    : feature.geometry.coordinates;
}

// Ray casting on [lng, lat] positions
function isInsideRing(
  [lng, lat]: SecurityGeoJsonPosition,
  ring: SecurityGeoJsonPosition[],
): boolean {
  let isInside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [lngI, latI] = ring[i];
    const [lngJ, latJ] = ring[j];
    const crosses =
      latI > lat !== latJ > lat &&
      lng < ((lngJ - lngI) * (lat - latI)) / (latJ - latI) + lngI;
    if (crosses) isInside = !isInside;
  }
  return isInside;
}

// West / east edges of the narrowest span holding every longitude: the edges sit at the
// widest gap between them (west > east crosses the 180° line)
function getLongitudeSpan(longitudes: number[]): { west: number; east: number } {
  const sorted = [...longitudes].sort((first, second) => first - second);
  let gapEndIndex = 0;
  let widestGap = sorted[0] + 360 - sorted[sorted.length - 1];
  for (let index = 1; index < sorted.length; index++) {
    const gap = sorted[index] - sorted[index - 1];
    if (gap > widestGap) {
      widestGap = gap;
      gapEndIndex = index;
    }
  }
  return {
    west: sorted[gapEndIndex],
    east: sorted[(gapEndIndex - 1 + sorted.length) % sorted.length],
  };
}
